import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Checkbox, Button, SearchField, Badge } from "@akuna/ui";
import { Switch } from "@akuna/ui";
import { Settings2 } from "lucide-react";
import type { AppointmentStatus } from "@/hooks/useAppointmentStatuses";

export type FilterOption = { value: string; label: string; color?: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  vetOptions: FilterOption[];
  selectedVets: string[];
  toggleVet: (vetId: string) => void;
  typeOptions: FilterOption[];
  selectedTypes: string[];
  toggleType: (type: string) => void;
  onlyTaxiDog: boolean;
  setOnlyTaxiDog: (v: boolean) => void;
  isKanbanView: boolean;
  statuses: AppointmentStatus[];
  isColumnVisible: (name: string) => boolean;
  toggleColumn: (name: string) => void;
  columnCounts: Record<string, number>;
  getStatusColor: (name: string, statuses: AppointmentStatus[]) => string;
  onClear: () => void;
};

export function MobileFlowboardFilterSheet(props: Props) {
  const { t } = useTranslation("calendar");
  const {
    open, onOpenChange, searchTerm, setSearchTerm,
    vetOptions, selectedVets, toggleVet,
    typeOptions, selectedTypes, toggleType,
    onlyTaxiDog, setOnlyTaxiDog,
    isKanbanView, statuses, isColumnVisible, toggleColumn, columnCounts, getStatusColor,
    onClear,
  } = props;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-3xl">
        <SheetHeader className="text-left">
          <SheetTitle>{t("flowboardMobile.filters.title")}</SheetTitle>
        </SheetHeader>
        <div className="space-y-5 py-4">
          <div className="space-y-2">
            <Label htmlFor="flowboard-search" className="text-sm font-semibold">
              {t("flowboardMobile.filters.search")}
            </Label>
            <SearchField
              id="flowboard-search"
              label={t("flowboardMobile.filters.search")}
              clearLabel={t("common:clearSearch")}
              placeholder={t("flowboardMobile.filters.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm("")}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold">{t("flowboardMobile.filters.professional")}</Label>
            <div className="space-y-1.5">
              {vetOptions.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={selectedVets.includes(opt.value)} onCheckedChange={() => toggleVet(opt.value)} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold">{t("flowboardMobile.filters.type")}</Label>
            <div className="space-y-1.5">
              {typeOptions.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={selectedTypes.includes(opt.value)} onCheckedChange={() => toggleType(opt.value)} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center justify-between text-sm font-semibold">
            {t("flowboardMobile.filters.taxiDog")}
            <Switch checked={onlyTaxiDog} onCheckedChange={setOnlyTaxiDog} />
          </label>

          {isKanbanView ? (
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5 text-sm font-semibold">
                <Settings2 className="h-3.5 w-3.5" />
                {t("flowboardMobile.filters.configureColumns")}
              </Label>
              <div className="space-y-1.5">
                {statuses.map((status) => (
                  <label key={status.name} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={isColumnVisible(status.name)} onCheckedChange={() => toggleColumn(status.name)} />
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: getStatusColor(status.name, statuses) }}
                    />
                    <span className="flex-1">{status.display_name}</span>
                    <Badge variant="default" className="text-xs">
                      {columnCounts[status.name] || 0}
                    </Badge>
                  </label>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex gap-2 pb-2">
          <Button variant="outline" className="flex-1" onClick={onClear}>
            {t("flowboardMobile.filters.clear")}
          </Button>
          <Button className="flex-1" onClick={() => onOpenChange(false)}>
            {t("flowboardMobile.filters.apply")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
